import { useEffect, useRef, useState } from 'react';
import { MutationKey, useIsMutating, useQueryClient } from '@tanstack/react-query';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import { VALUATION_SAVE_MUTATION_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { useIsMutationPending } from '@dealstack/frontend/shared/hooks/pendingMutations';
import { AutoSaveStatus } from './autoSaveStatusConfig';

function matchesSaveKey(
	key: MutationKey | undefined,
	pathValuationId: string | undefined,
	dealId: string | undefined
): boolean {
	if (!Array.isArray(key) || key.length < 2 || key[0] !== VALUATION_SAVE_MUTATION_KEY) {
		return false;
	}
	const target = key[1];
	return target === pathValuationId || target === dealId;
}

/**
 * Tracks the autosave status for the current valuation save group. Combines
 * three signals: useMutation in-flight count, debounce-pending count (from
 * pendingMutations.ts), and "ever saved" / latest error gleaned from the
 * mutation cache. Returns null while idle.
 */
export function useAutoSaveStatus(): AutoSaveStatus | null {
	const { pathValuationId, dealId } = useValuationNavigation();
	const queryClient = useQueryClient();

	const inflight =
		useIsMutating({ mutationKey: [VALUATION_SAVE_MUTATION_KEY, pathValuationId ?? ''] }) +
		useIsMutating({ mutationKey: [VALUATION_SAVE_MUTATION_KEY, dealId ?? ''] });
	const pending = useIsMutationPending([
		[VALUATION_SAVE_MUTATION_KEY, pathValuationId ?? ''],
		[VALUATION_SAVE_MUTATION_KEY, dealId ?? ''],
	]);

	const [hasError, setHasError] = useState(false);
	const [everSaved, setEverSaved] = useState(false);
	const everSavedRef = useRef(false);

	useEffect(() => {
		const cache = queryClient.getMutationCache();
		const unsubscribe = cache.subscribe((event) => {
			if (!matchesSaveKey(event?.mutation?.options.mutationKey, pathValuationId, dealId)) {
				return;
			}
			const status = event?.mutation?.state.status;
			if (status === 'error') {
				setHasError(true);
				return;
			}
			if (status === 'success') {
				setHasError(false);
				if (!everSavedRef.current) {
					everSavedRef.current = true;
					setEverSaved(true);
				}
			}
		});
		return () => unsubscribe();
	}, [queryClient, pathValuationId, dealId]);

	if (inflight > 0 || pending > 0) {
		return AutoSaveStatus.SAVING;
	}
	if (hasError) {
		return AutoSaveStatus.ERROR;
	}
	if (everSaved) {
		return AutoSaveStatus.SAVED;
	}
	return null;
}
